import React, { useState, useEffect } from 'react';

type Page = 'home' | 'about' | 'services' | 'hair' | 'skin' | 'bridal' | 'team' | 'gallery' | 'faq' | 'contact';

// === DUMMY DATA ===
const teamData = [
  { name: 'Ayesha Khan', role: 'Creative Director', img: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=400&auto=format&fit=crop' },
  { name: 'Rahul Verma', role: 'Senior Hairstylist', img: 'https://images.unsplash.com/photo-1618077360395-f3068be8e001?q=80&w=400&auto=format&fit=crop' },
  { name: 'Simran Kaur', role: 'Bridal Makeup Expert', img: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=400&auto=format&fit=crop' }
];

const faqs = [
  { q: 'Do I need to book an appointment in advance?', a: 'Yes, to ensure you receive the royal treatment without any waiting time, we highly recommend booking at least 24 hours in advance.' },
  { q: 'What brands of products do you use?', a: 'We exclusively use international premium brands including L\'Oréal Professionnel, Kérastase, M.A.C, and Bobbi Brown.' },
  { q: 'Do you offer bridal trials?', a: 'Absolutely. We offer paid bridal trials which are later adjusted against your final bridal package booking.' }
];

// === COMPONENT ===
const SalonPremium10Page: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<Page>('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  }, [currentPage]);

  // Contextual Header Component
  const PageHeader = ({ title, subtitle, bgImage }: { title: string, subtitle: string, bgImage: string }) => (
    <div className="bg-zinc-950 text-white py-28 px-6 text-center relative overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <img src={bgImage} className="w-full h-full object-cover" alt="Banner Background"/>
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-zinc-950 opacity-80"></div>
      <div className="relative z-10 max-w-3xl mx-auto animate-slideUp">
        <span className="text-amber-500 text-sm font-bold tracking-[0.3em] uppercase mb-4 block">Élégance Studio</span>
        <h1 className="text-5xl md:text-7xl font-serif mb-6 drop-shadow-lg">{title}</h1>
        <p className="text-zinc-300 text-lg drop-shadow-md">{subtitle}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-zinc-900 font-sans flex flex-col selection:bg-amber-900 selection:text-white">
      
      {/* --- SMART DROPDOWN NAVBAR --- */}
      <nav className="bg-zinc-950 text-white sticky top-0 z-50 border-b border-zinc-800 shadow-2xl">
        <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center relative">
          
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => setCurrentPage('home')}>
            <span className="text-3xl text-amber-500">♛</span>
            <span className="text-2xl font-serif tracking-wide text-white uppercase">
              Élégance <span className="font-light text-zinc-400 text-lg">Studio</span>
            </span>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8 h-full">
            <button onClick={() => setCurrentPage('home')} className={`h-full flex items-center text-xs font-bold uppercase tracking-[0.15em] transition-colors ${currentPage === 'home' ? 'text-amber-500' : 'text-zinc-300 hover:text-white'}`}>Home</button>
            
            {/* Shop Dropdown */}
            <div className="relative group h-full flex items-center">
              <button className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-300 group-hover:text-amber-500 transition-colors flex items-center gap-1">
                Shop <span className="text-[10px]">▼</span>
              </button>
              <div className="absolute top-20 left-0 w-48 bg-zinc-900 border border-zinc-800 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                <div onClick={() => setCurrentPage('about')} className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-zinc-400 hover:text-white hover:bg-amber-600 cursor-pointer border-b border-zinc-800 transition">About Us</div>
                <div onClick={() => setCurrentPage('team')} className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-zinc-400 hover:text-white hover:bg-amber-600 cursor-pointer border-b border-zinc-800 transition">Our Team</div>
                <div onClick={() => setCurrentPage('faq')} className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-zinc-400 hover:text-white hover:bg-amber-600 cursor-pointer transition">FAQ</div>
              </div>
            </div>

            {/* Service Dropdown */}
            <div className="relative group h-full flex items-center">
              <button onClick={() => setCurrentPage('services')} className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-300 group-hover:text-amber-500 transition-colors flex items-center gap-1">
                Services <span className="text-[10px]">▼</span>
              </button>
              <div className="absolute top-20 left-0 w-56 bg-zinc-900 border border-zinc-800 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                <div onClick={() => setCurrentPage('hair')} className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-zinc-400 hover:text-white hover:bg-amber-600 cursor-pointer border-b border-zinc-800 transition flex justify-between">Hair Care <span>✂️</span></div>
                <div onClick={() => setCurrentPage('skin')} className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-zinc-400 hover:text-white hover:bg-amber-600 cursor-pointer border-b border-zinc-800 transition flex justify-between">Skin & Spa <span>✨</span></div>
                <div onClick={() => setCurrentPage('bridal')} className="px-5 py-4 text-xs font-bold uppercase tracking-wider text-amber-500 hover:text-white hover:bg-amber-600 cursor-pointer transition flex justify-between">Bridal <span>👑</span></div>
              </div>
            </div>

            <button onClick={() => setCurrentPage('gallery')} className={`h-full flex items-center text-xs font-bold uppercase tracking-[0.15em] transition-colors ${currentPage === 'gallery' ? 'text-amber-500' : 'text-zinc-300 hover:text-white'}`}>Gallery</button>
            <button onClick={() => setCurrentPage('contact')} className={`h-full flex items-center text-xs font-bold uppercase tracking-[0.15em] transition-colors ${currentPage === 'contact' ? 'text-amber-500' : 'text-zinc-300 hover:text-white'}`}>Contact</button>
            
            <button onClick={() => setCurrentPage('contact')} className="ml-4 bg-amber-600 text-white px-6 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-amber-500 transition duration-300">
              Book VIP
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button className="md:hidden text-2xl text-amber-500" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>☰</button>
        </div>

        {/* Mobile Dropdown (Simplified) */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-zinc-900 border-t border-zinc-800 px-6 py-4 space-y-4">
            <div onClick={() => setCurrentPage('home')} className="text-sm font-bold uppercase tracking-wider text-white">Home</div>
            <div className="text-amber-500 text-xs font-bold uppercase tracking-wider pt-2 border-t border-zinc-800">Shop</div>
            <div onClick={() => setCurrentPage('about')} className="text-sm pl-4 text-zinc-400">About Us</div>
            <div onClick={() => setCurrentPage('team')} className="text-sm pl-4 text-zinc-400">Our Team</div>
            <div onClick={() => setCurrentPage('faq')} className="text-sm pl-4 text-zinc-400">FAQ</div>
            <div className="text-amber-500 text-xs font-bold uppercase tracking-wider pt-2 border-t border-zinc-800">Services</div>
            <div onClick={() => setCurrentPage('hair')} className="text-sm pl-4 text-zinc-400">Hair Care</div>
            <div onClick={() => setCurrentPage('skin')} className="text-sm pl-4 text-zinc-400">Skin & Spa</div>
            <div onClick={() => setCurrentPage('bridal')} className="text-sm pl-4 text-zinc-400">Bridal</div>
            <div className="border-t border-zinc-800 pt-4">
              <div onClick={() => setCurrentPage('gallery')} className="text-sm font-bold uppercase tracking-wider text-white mb-4">Gallery</div>
              <div onClick={() => setCurrentPage('contact')} className="text-sm font-bold uppercase tracking-wider text-white">Contact</div>
            </div>
          </div>
        )}
      </nav>

      {/* --- PAGE CONTENT --- */}
      <main className="flex-grow animate-fadeIn">
        
        {/* ================= 1. HOME PAGE (Expanded) ================= */}
        {currentPage === 'home' && (
          <div className="animate-slideUp pb-20">
            {/* Hero Section */}
            <div className="relative h-[85vh] flex items-center justify-center text-center">
              <div className="absolute inset-0 bg-zinc-950">
                <img src="https://images.unsplash.com/photo-1591316684784-24f61f5f3e79?q=80&w=1600&auto=format&fit=crop" alt="Hero" className="w-full h-full object-cover opacity-50"/>
              </div>
              <div className="relative z-10 max-w-4xl px-6 space-y-6">
                <span className="text-amber-500 font-bold tracking-[0.3em] uppercase text-sm drop-shadow-md">The Epitome of Beauty</span>
                <h1 className="text-6xl md:text-8xl font-serif text-white leading-tight drop-shadow-lg">Where Art <br/><span className="italic text-zinc-300">Meets Perfection.</span></h1>
                <p className="text-xl text-zinc-300 max-w-2xl mx-auto font-light">Experience world-class grooming, styling, and transformations in the heart of the city.</p>
                <div className="pt-8 flex justify-center gap-4">
                  <button onClick={() => setCurrentPage('services')} className="bg-amber-600 text-white px-10 py-4 font-bold uppercase tracking-widest hover:bg-amber-500 transition duration-300 text-sm">Our Menu</button>
                  <button onClick={() => setCurrentPage('contact')} className="border border-white text-white px-10 py-4 font-bold uppercase tracking-widest hover:bg-white hover:text-zinc-900 transition duration-300 text-sm">Book Session</button>
                </div>
              </div>
            </div>

            {/* Brand Story Snippet */}
            <div className="py-24 px-6 max-w-5xl mx-auto text-center space-y-8">
              <span className="text-amber-600 text-3xl">✧</span>
              <h2 className="text-4xl md:text-5xl font-serif text-zinc-900">A Legacy of Excellence</h2>
              <p className="text-zinc-600 text-xl leading-relaxed font-light">Since 2010, Élégance Studio has been the benchmark for premium salon experiences. We don't just cut hair or apply makeup; we craft confidence using the finest products the world has to offer.</p>
            </div>

            {/* Cinematic GIF/Video Placeholder Section */}
            <div className="w-full h-[60vh] md:h-[80vh] relative overflow-hidden group cursor-pointer" onClick={() => setCurrentPage('gallery')}>
              <img src="https://images.unsplash.com/photo-1522337660859-02fbefca4702?q=80&w=1600&auto=format&fit=crop" alt="Cinematic Salon Work" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
              <div className="absolute inset-0 bg-zinc-950/40 flex flex-col items-center justify-center transition-colors group-hover:bg-zinc-950/20">
                <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/50 mb-6">
                  <span className="text-white text-3xl ml-2">▶</span>
                </div>
                <h3 className="text-white text-4xl font-serif drop-shadow-lg">Experience The Magic</h3>
                <p className="text-white tracking-widest uppercase text-sm mt-2">Watch Our Portfolio</p>
              </div>
            </div>

            {/* Stats / Why Us */}
            <div className="bg-zinc-950 text-white py-20 px-6">
              <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-10 text-center divide-x divide-zinc-800">
                <div>
                  <h4 className="text-5xl font-serif text-amber-500 mb-2">12+</h4>
                  <p className="text-xs uppercase tracking-widest text-zinc-400">Years Experience</p>
                </div>
                <div>
                  <h4 className="text-5xl font-serif text-amber-500 mb-2">15k</h4>
                  <p className="text-xs uppercase tracking-widest text-zinc-400">Happy Clients</p>
                </div>
                <div>
                  <h4 className="text-5xl font-serif text-amber-500 mb-2">45</h4>
                  <p className="text-xs uppercase tracking-widest text-zinc-400">Expert Stylists</p>
                </div>
                <div>
                  <h4 className="text-5xl font-serif text-amber-500 mb-2">5★</h4>
                  <p className="text-xs uppercase tracking-widest text-zinc-400">Rated Service</p>
                </div>
              </div>
            </div>

            {/* Luxury Testimonials */}
            <div className="py-24 px-6 bg-zinc-50">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-serif text-zinc-900">Words of Elegance</h2>
              </div>
              <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
                {[
                  { text: "The bridal package was an absolute dream. The team treated me like royalty, and the makeup lasted flawlessly all night.", author: "Priyanka S." },
                  { text: "My go-to place for Keratin. The ambience is incredibly relaxing, and the staff is highly professional.", author: "Neha Kapoor" },
                  { text: "Unmatched expertise. I walked in for a simple trim and walked out with a completely transformed, confident look.", author: "Ananya R." }
                ].map((review, idx) => (
                  <div key={idx} className="bg-white p-10 border border-zinc-200 shadow-xl relative">
                    <span className="text-6xl text-amber-100 absolute top-4 left-4 font-serif">"</span>
                    <p className="text-zinc-600 italic relative z-10 leading-relaxed mb-6">"{review.text}"</p>
                    <div className="border-t border-zinc-100 pt-4">
                      <span className="font-bold text-zinc-900 uppercase tracking-wider text-xs">{review.author}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ================= OTHER PAGES (With Contextual Banners) ================= */}
        
        {currentPage === 'about' && (
          <div className="animate-slideUp">
            <PageHeader title="Our Story" subtitle="Redefining luxury beauty since 2010." bgImage="https://images.unsplash.com/photo-1600952841320-db92ec4047ca?q=80&w=1600&auto=format&fit=crop" />
            <div className="py-24 px-6 max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
              <div className="aspect-[4/5] bg-zinc-200">
                <img src="https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?q=80&w=800&auto=format&fit=crop" alt="Founder" className="w-full h-full object-cover grayscale hover:grayscale-0 transition duration-700"/>
              </div>
              <div className="space-y-6">
                <h2 className="text-4xl font-serif">The Visionary Behind The Brand</h2>
                <p className="text-zinc-600 leading-relaxed text-lg">Founded by celebrity stylist Anya Sharma, Élégance Studio was born out of a desire to create a sanctuary where clients could escape the chaos of the city and indulge in pure pampering.</p>
                <div className="border-l-4 border-amber-500 pl-6 my-8">
                  <p className="text-2xl font-serif italic text-zinc-800">"Beauty is power, and a smile is its sword."</p>
                </div>
                <button onClick={() => setCurrentPage('team')} className="bg-zinc-950 text-white px-8 py-4 font-bold uppercase tracking-widest hover:bg-zinc-800 transition text-sm">Meet Our Team</button>
              </div>
            </div>
          </div>
        )}

        {currentPage === 'services' && (
          <div className="animate-slideUp">
            <PageHeader title="Our Services" subtitle="Choose your path to transformation." bgImage="https://images.unsplash.com/photo-1516975080661-46bfa99281a8?q=80&w=1600&auto=format&fit=crop" />
            <div className="py-24 px-6 max-w-7xl mx-auto grid md:grid-cols-3 gap-8">
              {[
                { title: 'Hair Artistry', img: 'https://images.unsplash.com/photo-1596178060671-7a80dc8059ea?q=80&w=600', link: 'hair' },
                { title: 'Skin & Spa', img: 'https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?q=80&w=600', link: 'skin' },
                { title: 'Bridal Packages', img: 'https://images.unsplash.com/photo-1595152452543-e5fc28ebc2b8?q=80&w=600', link: 'bridal' }
              ].map((svc) => (
                <div key={svc.title} className="group cursor-pointer" onClick={() => setCurrentPage(svc.link as Page)}>
                  <div className="aspect-[3/4] overflow-hidden mb-6"><img src={svc.img} alt={svc.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700"/></div>
                  <h3 className="text-3xl font-serif text-zinc-900 mb-2">{svc.title}</h3>
                  <p className="text-amber-600 font-bold uppercase tracking-widest text-xs group-hover:text-zinc-900 transition">Explore Menu →</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {currentPage === 'hair' && (
          <div className="animate-slideUp">
            <PageHeader title="Hair Artistry" subtitle="Precision cuts and vibrant colors." bgImage="https://images.unsplash.com/photo-1562322140-8baeececf3df?q=80&w=1600&auto=format&fit=crop" />
            <div className="py-24 px-6 max-w-4xl mx-auto">
              <ul className="space-y-8">
                {[
                  { n: 'Director\'s Cut & Style', p: '₹1,500', d: 'Consultation, scalp massage, precision cut, and blow-dry by our top director.' },
                  { n: 'Balayage Coloring', p: '₹6,500+', d: 'Hand-painted highlights for a natural, sun-kissed look. Includes Olaplex treatment.' },
                  { n: 'Keratin Smoothing', p: '₹5,000+', d: 'Eliminate frizz and add intense shine for up to 4 months.' }
                ].map((item, idx) => (
                  <li key={idx} className="border-b border-zinc-200 pb-8">
                    <div className="flex justify-between items-end mb-2"><h3 className="text-2xl font-serif text-zinc-900">{item.n}</h3><span className="text-xl font-bold text-amber-600">{item.p}</span></div>
                    <p className="text-zinc-600">{item.d}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {currentPage === 'skin' && (
          <div className="animate-slideUp">
            <PageHeader title="Skin & Spa" subtitle="Rejuvenate, relax, and glow." bgImage="https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?q=80&w=1600&auto=format&fit=crop" />
            <div className="py-24 px-6 max-w-4xl mx-auto">
              <ul className="space-y-8">
                {[
                  { n: 'Signature Gold Facial', p: '₹3,500', d: 'Infused with 24K gold dust for an immediate, lifting glow.' },
                  { n: 'Deep Tissue Massage', p: '₹2,800', d: 'Targeted muscle relief using warm essential oils (60 Min).' },
                  { n: 'Advanced HydraFacial', p: '₹4,500', d: 'Medical-grade skin resurfacing and deep hydration.' }
                ].map((item, idx) => (
                  <li key={idx} className="border-b border-zinc-200 pb-8">
                    <div className="flex justify-between items-end mb-2"><h3 className="text-2xl font-serif text-zinc-900">{item.n}</h3><span className="text-xl font-bold text-amber-600">{item.p}</span></div>
                    <p className="text-zinc-600">{item.d}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {currentPage === 'bridal' && (
          <div className="animate-slideUp">
            <PageHeader title="The Bridal Suite" subtitle="Your perfect day deserves perfect artistry." bgImage="https://images.unsplash.com/photo-1595152452543-e5fc28ebc2b8?q=80&w=1600&auto=format&fit=crop" />
            <div className="py-24 px-6 max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-serif mb-6">The Royal Package</h2>
                <p className="text-zinc-600 mb-8 text-lg">From pre-wedding skin prep to the final dupatta draping, our premium package covers everything.</p>
                <div className="bg-zinc-50 p-8 border border-zinc-200">
                  <span className="block text-3xl font-bold text-amber-600 mb-4">₹25,000</span>
                  <ul className="space-y-3 text-zinc-700 list-disc list-inside">
                    <li>HD Airbrush Makeup</li><li>Advanced Hair Styling</li><li>Saree / Lehenga Draping</li><li>Premium Mink Lashes</li>
                  </ul>
                </div>
              </div>
              <img src="https://images.unsplash.com/photo-1595152772835-219674b2a8a6?q=80&w=800&auto=format&fit=crop" alt="Bride" className="w-full object-cover"/>
            </div>
          </div>
        )}

        {currentPage === 'team' && (
          <div className="animate-slideUp">
            <PageHeader title="Our Experts" subtitle="The artists behind the magic." bgImage="https://images.unsplash.com/photo-1522337660859-02fbefca4702?q=80&w=1600&auto=format&fit=crop" />
            <div className="py-24 px-6 max-w-7xl mx-auto grid md:grid-cols-3 gap-10">
              {teamData.map((member, idx) => (
                <div key={idx} className="text-center group">
                  <div className="aspect-square overflow-hidden mb-6 bg-zinc-200 rounded-full w-64 h-64 mx-auto border-4 border-white shadow-xl">
                    <img src={member.img} alt={member.name} className="w-full h-full object-cover group-hover:scale-110 transition duration-500 grayscale group-hover:grayscale-0"/>
                  </div>
                  <h3 className="text-2xl font-serif text-zinc-900">{member.name}</h3>
                  <p className="text-amber-600 font-bold tracking-widest text-xs uppercase mt-2">{member.role}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {currentPage === 'gallery' && (
          <div className="animate-slideUp">
            <PageHeader title="Portfolio" subtitle="A collection of our proudest transformations." bgImage="https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?q=80&w=1600&auto=format&fit=crop" />
            <div className="py-24 px-6 max-w-7xl mx-auto columns-2 md:columns-3 gap-4 space-y-4">
              {['1560066984-138dadb4c035', '1521590832167-7bcbfaa6381f', '1616394584738-fc6e612e71b9', '1516975080661-46bfa99281a8', '1596178060671-7a80dc8059ea', '1522337660859-02fbefca4702'].map((id, idx) => (
                <div key={idx} className="break-inside-avoid relative group overflow-hidden bg-zinc-900">
                  <img src={`https://images.unsplash.com/photo-${id}?q=80&w=600`} alt={`Gallery ${idx}`} className="w-full h-auto object-cover group-hover:opacity-50 transition duration-500"/>
                </div>
              ))}
            </div>
          </div>
        )}

        {currentPage === 'faq' && (
          <div className="animate-slideUp">
            <PageHeader title="FAQ" subtitle="Common questions answered for you." bgImage="https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?q=80&w=1600&auto=format&fit=crop" />
            <div className="py-24 px-6 max-w-3xl mx-auto space-y-8">
              {faqs.map((faq, idx) => (
                <div key={idx} className="border-b border-zinc-200 pb-6">
                  <h3 className="text-xl font-serif font-bold text-zinc-900 mb-3">Q. {faq.q}</h3>
                  <p className="text-zinc-600 leading-relaxed">A. {faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {currentPage === 'contact' && (
          <div className="animate-slideUp">
            <PageHeader title="Reserve a Session" subtitle="We look forward to hosting you." bgImage="https://images.unsplash.com/photo-1600952841320-db92ec4047ca?q=80&w=1600&auto=format&fit=crop" />
            <div className="py-24 px-6 max-w-7xl mx-auto grid md:grid-cols-2 gap-16">
              <div className="bg-white p-10 border border-zinc-200 shadow-xl">
                <h2 className="text-3xl font-serif mb-8">Booking Details</h2>
                <form className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div><label className="block text-xs font-bold uppercase text-zinc-500 mb-2">First Name</label><input type="text" className="w-full border-b-2 border-zinc-200 py-2 focus:border-amber-600 outline-none" required/></div>
                    <div><label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Last Name</label><input type="text" className="w-full border-b-2 border-zinc-200 py-2 focus:border-amber-600 outline-none" required/></div>
                  </div>
                  <div><label className="block text-xs font-bold uppercase text-zinc-500 mb-2">Phone</label><input type="tel" className="w-full border-b-2 border-zinc-200 py-2 focus:border-amber-600 outline-none" required/></div>
                  <button type="button" className="w-full bg-zinc-950 text-white py-5 uppercase font-bold hover:bg-amber-600 transition">Confirm Request</button>
                </form>
              </div>
              <div className="space-y-10 flex flex-col justify-center">
                <div><h3 className="text-xs font-bold uppercase text-amber-600 mb-3">Location</h3><p className="text-2xl font-serif text-zinc-900">101, Luxury Boulevard,<br/>New Delhi.</p></div>
                <div><h3 className="text-xs font-bold uppercase text-amber-600 mb-3">Contact</h3><p className="text-2xl font-serif text-zinc-900">+91 99999 00000</p></div>
              </div>
            </div>
          </div>
        )}

      </main>

      {/* --- MEGA FOOTER --- */}
      <footer className="bg-zinc-950 text-zinc-400 py-20 mt-auto border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-4 gap-12">
          <div className="col-span-1 md:col-span-2 space-y-6">
            <span className="text-3xl text-amber-500 block mb-4">♛</span>
            <h2 className="text-2xl font-serif text-white">Élégance Studio</h2>
            <p className="max-w-md text-sm leading-relaxed">The pinnacle of luxury grooming and styling. We are dedicated to providing an unparalleled experience in a serene environment.</p>
          </div>
          <div>
            <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-6">Explore</h4>
            <ul className="space-y-4 text-sm">
              <li><button onClick={() => setCurrentPage('about')} className="hover:text-amber-500">About</button></li>
              <li><button onClick={() => setCurrentPage('services')} className="hover:text-amber-500">Services</button></li>
              <li><button onClick={() => setCurrentPage('gallery')} className="hover:text-amber-500">Gallery</button></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-6">Legal</h4>
            <ul className="space-y-4 text-sm">
              <li><a href="#" className="hover:text-amber-500">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-amber-500">Terms of Service</a></li>
            </ul>
          </div>
        </div>
      </footer>
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-slideUp { animation: slideUp 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-fadeIn { animation: fadeIn 0.5s ease-out forwards; }
      `}} />
    </div>
  );
};

export default SalonPremium10Page;